import React from "react";
import PropTypes from "prop-types";
import UnstyledImage from "./image";
import styled from "styled-components";

const Wrapper = styled.div`
  position: relative;
  min-height: clamp(40vh, 40vw, 800px);
  height: 100%;
  width: 100%;
`;
const InnerWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
`;
const Image = styled(UnstyledImage)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;
const Children = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-items: ${({ $alignContent }) => $alignContent || "center"};
  padding: 3rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-inline: 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    width: fit-content;
    font-family: ${({ theme }) => theme.fontHeadings.fontFamily};
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 1.5rem;
    }
  }
  a {
    font-family: ${({ theme }) => theme.fontLinks.fontFamily};
    width: fit-content;
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 1rem;
    }
    color: ${({ $linkColor }) => $linkColor};
  }
  p {
    width: fit-content;
    font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 1rem;
    }
  }
  letter-spacing: ${({ $letterSpacing }) => $letterSpacing + "px"};
  line-height: ${({ $rowSpacing }) => $rowSpacing};
`;

const Video = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoFrame = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  z-index: -1;
`;
const IconDiv = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
`;

const SuperheroSlide = ({
  children,
  alignContent,
  media,
  image,
  imageUrl,
  letterSpacing,
  rowSpacing,
  linkColor,
  className,
  ...props
}) => {
  const [isVisible, setVisible] = React.useState(true);
  const [play, setPlay] = React.useState(false);
  const playVid = () => {
    if (media && media.mimeType === "video/mp4") {
      setVisible(false);
      setPlay(true);
    }
  };

  if (media && media.mimeType === "image/jpeg") {
    return (
      <Wrapper {...props}>
        <Image image={media} src={media.mediaItemUrl} loading="eager" />
        <InnerWrapper>
          <Children $alignContent={alignContent} className={className}>
            {children}
          </Children>
        </InnerWrapper>
      </Wrapper>
    );
  } else if (media && media.mimeType === "video/mp4") {
    if (image || imageUrl) {
      return (
        <Wrapper {...props}>
          <Video>
            {play && (
              <VideoFrame controls autoPlay muted loop playsInline>
                <source src={media.mediaItemUrl} type="video/mp4" />
              </VideoFrame>
            )}
            {isVisible && (
              <>
                <Image src={imageUrl} image={image} loading="eager" />
                <IconDiv onClick={() => playVid()}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="58"
                    height="68"
                    viewBox="0 0 58 68"
                  >
                    <path
                      id="Polygon_21"
                      data-name="Polygon 21"
                      d="M34,0,68,58H0Z"
                      transform="translate(58) rotate(90)"
                      fill="#fff"
                    />
                  </svg>
                </IconDiv>
              </>
            )}
          </Video>
          <InnerWrapper>
            <Children
              $alignContent={alignContent}
              $letterSpacing={letterSpacing}
              $rowSpacing={rowSpacing}
              $linkColor={linkColor}
              className={className}
            >
              {children}
            </Children>
          </InnerWrapper>
        </Wrapper>
      );
    } else {
      return (
        <Wrapper {...props}>
          <Video>
            <VideoFrame autoPlay muted loop playsInline>
              <source src={media.mediaItemUrl} type="video/mp4" />
            </VideoFrame>
          </Video>
          <InnerWrapper>
            <Children
              $alignContent={alignContent}
              $letterSpacing={letterSpacing}
              $rowSpacing={rowSpacing}
              $linkColor={linkColor}
              className={className}
            >
              {children}
            </Children>
          </InnerWrapper>
        </Wrapper>
      );
    }
  }
};

SuperheroSlide.propTypes = {
  children: PropTypes.node,
  alignContent: PropTypes.string,
  media: PropTypes.object,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  letterSpacing: PropTypes.string,
  rowSpacing: PropTypes.string,
  linkColor: PropTypes.string,
  className: PropTypes.string,
};

export default SuperheroSlide;
